import React, { useState, useMemo } from 'react';
import {
    Zap, DollarSign, Film, Activity, Target, AlertTriangle,
    CheckCircle, ChevronRight, TrendingUp, TrendingDown,
    BarChart2, Brain, Loader2, Download, RefreshCw,
    Eye, Share2, Bookmark, MessageSquare, Clock, Layers,
    ArrowRight, Info, Radio, FlaskConical, Plus, Trash2,
    Trophy, XCircle, Scissors, TrendingUp as Scale
} from 'lucide-react';

import { generateAndromedaSimulationLLM, generateAndromedaDebriefLLM } from '../../services/aiService';
import { AppButton } from '../../components/SharedUI';

// ─── DESIGN TOKENS ────────────────────────────────────────────
const PLATFORMS = ['Instagram', 'Facebook', 'TikTok'];
const OBJECTIVES = [
    { value: 'video_views',  label: 'Video Views (ThruPlay)',   desc: 'Best for trailers & BTS clips' },
    { value: 'traffic',      label: 'Traffic (Link Clicks)',     desc: 'Best for driving to site/trailer' },
    { value: 'awareness',    label: 'Reach & Awareness',         desc: 'Best for pure brand signal' },
];

// ─── SMALL SHARED COMPONENTS ──────────────────────────────────

const SectionHeader = ({ label, icon: Icon, color = 'text-amber-400' }) => (
    <div className="flex items-center gap-2 mb-4">
        <Icon size={13} className={color} />
        <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400">{label}</h3>
    </div>
);

const StatCell = ({ label, value, sub, accent = false }) => (
    <div className={`p-4 rounded-xl border ${accent ? 'bg-amber-500/8 border-amber-500/25' : 'bg-zinc-900/50 border-zinc-800'}`}>
        <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
        <p className={`text-lg font-black leading-none ${accent ? 'text-amber-300' : 'text-white'}`}>{value}</p>
        {sub && <p className="text-[10px] text-zinc-500 mt-1 leading-snug">{sub}</p>}
    </div>
);

const Tag = ({ children, variant = 'neutral' }) => {
    const cls = {
        green:   'bg-green-500/10 text-green-300 border-green-500/25',
        red:     'bg-red-500/10 text-red-300 border-red-500/25',
        amber:   'bg-amber-500/10 text-amber-300 border-amber-500/25',
        blue:    'bg-blue-500/10 text-blue-300 border-blue-500/25',
        neutral: 'bg-zinc-800 text-zinc-400 border-zinc-700',
    }[variant];
    return (
        <span className={`inline-block text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${cls}`}>
            {children}
        </span>
    );
};

const Pill = ({ value, label, color }) => (
    <div className="text-center">
        <p className={`text-2xl font-black ${color}`}>{value}</p>
        <p className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">{label}</p>
    </div>
);

// ─── PHASE CARD ────────────────────────────────────────────────
const PhaseCard = ({ phase, index }) => {
    const [open, setOpen] = useState(index === 0);

    const phaseColor = index === 0 ? 'border-l-zinc-600' :
                       index === 1 ? 'border-l-amber-500' :
                       index === 2 ? 'border-l-green-500' : 'border-l-blue-500';

    return (
        <div className={`border border-zinc-800 border-l-4 ${phaseColor} rounded-xl overflow-hidden`}>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-4 px-5 py-4 bg-zinc-900/50 hover:bg-zinc-900 transition-colors text-left"
            >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${
                    index === 0 ? 'bg-zinc-700 text-zinc-300' :
                    index === 1 ? 'bg-amber-500/20 text-amber-300' :
                    index === 2 ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300'
                }`}>{index + 1}</div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-white text-sm">{phase.phase_name}</h4>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{phase.days}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right hidden md:block">
                        <p className="text-[9px] text-zinc-600 uppercase tracking-widest">CPR</p>
                        <p className="text-xs font-bold text-zinc-300">{phase.expected_metrics?.cpr_range}</p>
                    </div>
                    <ChevronRight size={14} className={`text-zinc-600 transition-transform ${open ? 'rotate-90' : ''}`} />
                </div>
            </button>

            {open && (
                <div className="px-5 py-5 space-y-5 border-t border-zinc-800/60 animate-in fade-in slide-in-from-top-1 duration-200">
                    {/* What's happening */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">What Andromeda is doing</p>
                            <p className="text-xs text-zinc-300 leading-relaxed">{phase.what_andromeda_is_doing}</p>
                        </div>
                        <div>
                            <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">What you will see in your dashboard</p>
                            <p className="text-xs text-zinc-300 leading-relaxed">{phase.what_you_will_see}</p>
                        </div>
                    </div>

                    {/* Metrics row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <StatCell label="CPR Range"      value={phase.expected_metrics?.cpr_range} />
                        <StatCell label="Results / Day"  value={phase.expected_metrics?.daily_results} />
                        <StatCell label="Daily Reach"    value={phase.expected_metrics?.reach} />
                        <StatCell label="Frequency"      value={phase.expected_metrics?.frequency} />
                    </div>

                    {/* Signals */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-4 bg-green-500/5 border border-green-500/15 rounded-xl">
                            <p className="text-[9px] font-bold text-green-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                                <CheckCircle size={9} /> Green Signals — It's Working
                            </p>
                            <ul className="space-y-1.5">
                                {phase.green_signals?.map((s, i) => (
                                    <li key={i} className="text-[11px] text-zinc-300 flex gap-2">
                                        <span className="text-green-500 shrink-0 mt-0.5">›</span>{s}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-4 bg-red-500/5 border border-red-500/15 rounded-xl">
                            <p className="text-[9px] font-bold text-red-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                                <AlertTriangle size={9} /> Red Signals — Take Action
                            </p>
                            <ul className="space-y-1.5">
                                {phase.red_signals?.map((s, i) => (
                                    <li key={i} className="text-[11px] text-zinc-300 flex gap-2">
                                        <span className="text-red-400 shrink-0 mt-0.5">›</span>{s}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Decision */}
                    <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/15 rounded-xl">
                        <ArrowRight size={13} className="text-amber-400 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-1">Decision at end of phase</p>
                            <p className="text-xs text-zinc-200 leading-relaxed">{phase.decision}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── SCENARIO CARD ─────────────────────────────────────────────
const ScenarioCard = ({ label, data, variant }) => {
    const styles = {
        conservative: { border: 'border-zinc-700',     accent: 'text-zinc-400',  bg: 'bg-zinc-900/30',    tag: 'neutral' },
        realistic:    { border: 'border-amber-500/40',  accent: 'text-amber-300', bg: 'bg-amber-500/5',    tag: 'amber'   },
        breakout:     { border: 'border-green-500/40',  accent: 'text-green-300', bg: 'bg-green-500/5',    tag: 'green'   },
    }[variant];

    return (
        <div className={`border ${styles.border} ${styles.bg} rounded-xl p-5 flex flex-col gap-4`}>
            <div className="flex items-center justify-between">
                <h4 className={`text-sm font-black ${styles.accent}`}>{data.label}</h4>
                <Tag variant={styles.tag}>{label}</Tag>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed italic">"{data.conditions}"</p>
            <div className="grid grid-cols-2 gap-2">
                <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-1">Video Views</p>
                    <p className={`text-sm font-bold ${styles.accent}`}>{data.video_views}</p>
                </div>
                <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-1">Link Clicks</p>
                    <p className={`text-sm font-bold ${styles.accent}`}>{data.link_clicks}</p>
                </div>
                <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-1">Shares + Saves</p>
                    <p className={`text-sm font-bold ${styles.accent}`}>{data.shares_saves}</p>
                </div>
                <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-1">Clusters Found</p>
                    <p className={`text-sm font-bold ${styles.accent}`}>{data.audience_clusters_found}</p>
                </div>
            </div>
            <div className="flex items-start gap-2 pt-1 border-t border-white/5">
                <Info size={11} className={`${styles.accent} shrink-0 mt-0.5`} />
                <p className="text-[11px] text-zinc-400 leading-relaxed">{data.filmmaker_takeaway}</p>
            </div>
        </div>
    );
};

// ─── WATCH LIST ROW ────────────────────────────────────────────
const WatchRow = ({ item, index }) => (
    <div className="grid grid-cols-12 gap-3 py-3.5 border-b border-zinc-800/60 last:border-0 items-start">
        <div className="col-span-1 text-[10px] font-black text-zinc-700 pt-0.5">{String(index+1).padStart(2,'0')}</div>
        <div className="col-span-3">
            <p className="text-xs font-bold text-white">{item.metric}</p>
        </div>
        <div className="col-span-2">
            <Tag variant="green">{item.healthy_range}</Tag>
        </div>
        <div className="col-span-2">
            <Tag variant="red">{item.danger_zone}</Tag>
        </div>
        <div className="col-span-4">
            <p className="text-[11px] text-zinc-400 leading-relaxed">{item.what_it_means}</p>
        </div>
    </div>
);

// ─── DECISION TRIGGER ──────────────────────────────────────────
const DecisionTrigger = ({ item, index }) => (
    <div className="relative pl-8">
        <div className="absolute left-0 top-0.5 w-5 h-5 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-[9px] font-black text-amber-400">
            {index + 1}
        </div>
        <div className="space-y-3">
            <div>
                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-1">Trigger · {item.timing}</p>
                <p className="text-xs font-bold text-white">{item.trigger}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="p-3 bg-green-500/5 border border-green-500/15 rounded-lg">
                    <p className="text-[9px] font-bold text-green-400 uppercase tracking-widest mb-1">Do this</p>
                    <p className="text-[11px] text-zinc-300 leading-relaxed">{item.action}</p>
                </div>
                <div className="p-3 bg-red-500/5 border border-red-500/15 rounded-lg">
                    <p className="text-[9px] font-bold text-red-400 uppercase tracking-widest mb-1">Not this</p>
                    <p className="text-[11px] text-zinc-300 leading-relaxed">{item.do_not}</p>
                </div>
            </div>
        </div>
    </div>
);

// ─── LOADING OVERLAY ──────────────────────────────────────────
const SimulatorOverlay = ({ isVisible }) => {
    const steps = [
        "Loading campaign configuration...",
        "Modeling learning phase velocity...",
        "Calculating outcome scenarios...",
        "Mapping decision triggers...",
        "Compiling intelligence harvest...",
    ];
    const [step, setStep] = React.useState(0);
    React.useEffect(() => {
        if (!isVisible) return;
        setStep(0);
        const t = setInterval(() => setStep(p => Math.min(p + 1, steps.length - 1)), 1800);
        return () => clearInterval(t);
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/96 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-md p-8">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-amber-500 blur-2xl opacity-15 animate-pulse rounded-full" />
                        <Zap className="w-16 h-16 text-amber-400 relative z-10 animate-pulse" />
                    </div>
                </div>
                <div className="w-full h-0.5 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all duration-700"
                        style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
                </div>
                <div className="space-y-3 font-mono text-xs">
                    {steps.map((s, i) => (
                        <div key={i} className={`flex items-center gap-3 transition-all ${i === step ? 'text-white font-bold' : i < step ? 'text-zinc-600' : 'text-zinc-800'}`}>
                            <div className="w-4 flex justify-center shrink-0">
                                {i < step  ? <CheckCircle size={12} className="text-amber-500" />
                               : i === step ? <Loader2 size={12} className="animate-spin" />
                               : <div className="w-2 h-2 rounded-full bg-zinc-800" />}
                            </div>
                            {s}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};


// ─── ASSET DATA ENTRY ROW ──────────────────────────────────────
const EMPTY_ASSET = () => ({
    id: Date.now() + Math.random(),
    name: '', format: 'Vertical Video', hook: '',
    spend: '', results: '', cpr: '', reach: '', frequency: '',
    thruplay_rate: '', ctr: '', shares: '', saves: '', comments: '',
    video_25: '', video_75: '', video_95: '',
});

const FORMATS = ['Vertical Video', 'Carousel', 'Static Image'];

const AssetRow = ({ asset, hooks, onChange, onRemove, index }) => {
    const update = (field, val) => onChange({ ...asset, [field]: val });
    const inp = (field, placeholder, width = 'w-20') =>
        <input
            value={asset[field]}
            onChange={e => update(field, e.target.value)}
            placeholder={placeholder}
            className={`${width} bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/40 placeholder:text-zinc-700`}
        />;

    return (
        <div className="border border-zinc-800 rounded-xl p-4 space-y-3 bg-zinc-900/20 hover:border-zinc-700 transition-colors">
            {/* Row 1: identity */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="w-5 h-5 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-black text-amber-400 flex items-center justify-center shrink-0">
                    {index + 1}
                </div>
                <input
                    value={asset.name}
                    onChange={e => update('name', e.target.value)}
                    placeholder="Asset / Ad name (match Meta exactly)"
                    className="flex-1 min-w-[180px] bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-3 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/40 placeholder:text-zinc-700 font-bold"
                />
                <select value={asset.format} onChange={e => update('format', e.target.value)}
                    className="bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/40">
                    {FORMATS.map(f => <option key={f}>{f}</option>)}
                </select>
                <select value={asset.hook} onChange={e => update('hook', e.target.value)}
                    className="flex-1 min-w-[140px] bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/40">
                    <option value="">— Hook —</option>
                    {hooks.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <button onClick={onRemove} className="text-zinc-700 hover:text-red-400 transition-colors p-1">
                    <Trash2 size={13} />
                </button>
            </div>
            {/* Row 2: core metrics */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                    ['spend',      'Spend $'],
                    ['results',    'Results'],
                    ['cpr',        'CPR $'],
                    ['reach',      'Reach'],
                    ['frequency',  'Freq.'],
                    ['ctr',        'CTR %'],
                ].map(([field, label]) => (
                    <div key={field}>
                        <p className="text-[8px] font-bold text-zinc-600 uppercase tracking-widest mb-1">{label}</p>
                        {inp(field, label, 'w-full')}
                    </div>
                ))}
            </div>
            {/* Row 3: quality signals */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                    ['shares',         'Shares'],
                    ['saves',          'Saves'],
                    ['comments',       'Comments'],
                    ['thruplay_rate',  'ThruPlay %'],
                    ['video_75',       'Video 75%'],
                    ['video_95',       'Video 95%'],
                ].map(([field, label]) => (
                    <div key={field}>
                        <p className="text-[8px] font-bold text-zinc-600 uppercase tracking-widest mb-1">{label}</p>
                        {inp(field, label, 'w-full')}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── VERDICT BADGE ──────────────────────────────────────────────
const VerdictBadge = ({ verdict }) => {
    const map = {
        Scale:  { cls: 'bg-green-500/15 text-green-300 border-green-500/30',  icon: TrendingUp  },
        Hold:   { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',  icon: Activity    },
        Remix:  { cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30',     icon: Scissors    },
        Kill:   { cls: 'bg-red-500/15 text-red-300 border-red-500/30',        icon: XCircle     },
    }[verdict] || { cls: 'bg-zinc-800 text-zinc-400 border-zinc-700', icon: Activity };
    const Icon = map.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${map.cls}`}>
            <Icon size={9} /> {verdict}
        </span>
    );
};

const SignalBadge = ({ signal }) => {
    const map = {
        Strong:   'bg-green-500/10 text-green-300 border-green-500/25',
        Moderate: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
        Weak:     'bg-orange-500/10 text-orange-300 border-orange-500/25',
        Dead:     'bg-red-500/10 text-red-300 border-red-500/25',
    };
    return (
        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${map[signal] || 'bg-zinc-800 text-zinc-500 border-zinc-700'}`}>
            {signal}
        </span>
    );
};

// ─── ASSET RANKING CARD ─────────────────────────────────────────
const AssetRankCard = ({ asset }) => (
    <div className={`border rounded-xl p-5 space-y-3 ${
        asset.verdict === 'Scale' ? 'border-green-500/30 bg-green-500/5' :
        asset.verdict === 'Kill'  ? 'border-red-500/20 bg-red-500/5 opacity-75' :
        asset.verdict === 'Remix' ? 'border-blue-500/25 bg-blue-500/5' :
        'border-zinc-800 bg-zinc-900/30'
    }`}>
        <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700 text-[10px] font-black text-zinc-400 flex items-center justify-center shrink-0">
                    {asset.rank}
                </div>
                <h4 className="font-bold text-white text-sm truncate">{asset.asset_name}</h4>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <SignalBadge signal={asset.andromeda_signal_score} />
                <VerdictBadge verdict={asset.verdict} />
            </div>
        </div>
        <div className="space-y-2">
            <p className="text-xs text-zinc-300 leading-relaxed">{asset.why_it_won_or_lost}</p>
            <div className="flex items-start gap-2 p-2.5 bg-black/20 rounded-lg border border-white/5">
                <span className="text-[9px] font-black text-amber-400 uppercase tracking-widest shrink-0 mt-0.5">The Tell:</span>
                <span className="text-[11px] text-zinc-300 leading-relaxed">{asset.the_tell}</span>
            </div>
            <div className="flex items-start gap-2 pt-1">
                <ArrowRight size={11} className="text-amber-400 shrink-0 mt-0.5" />
                <span className="text-[11px] text-amber-200 leading-relaxed font-medium">{asset.action}</span>
            </div>
        </div>
    </div>
);

// ─── DEBRIEF LOADING OVERLAY ────────────────────────────────────
const DebriefOverlay = ({ isVisible }) => {
    const steps = [
        "Ingesting campaign performance data...",
        "Scoring assets by Andromeda signal quality...",
        "Identifying validated and rejected hooks...",
        "Building budget reallocation directive...",
        "Generating next campaign briefs...",
    ];
    const [step, setStep] = React.useState(0);
    React.useEffect(() => {
        if (!isVisible) return;
        setStep(0);
        const t = setInterval(() => setStep(p => Math.min(p + 1, steps.length - 1)), 1800);
        return () => clearInterval(t);
    }, [isVisible]);

    if (!isVisible) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/96 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-md p-8">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-blue-500 blur-2xl opacity-15 animate-pulse rounded-full" />
                        <FlaskConical className="w-16 h-16 text-blue-400 relative z-10 animate-pulse" />
                    </div>
                </div>
                <div className="w-full h-0.5 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all duration-700"
                        style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
                </div>
                <div className="space-y-3 font-mono text-xs">
                    {steps.map((s, i) => (
                        <div key={i} className={`flex items-center gap-3 transition-all ${i === step ? 'text-white font-bold' : i < step ? 'text-zinc-600' : 'text-zinc-800'}`}>
                            <div className="w-4 flex justify-center shrink-0">
                                {i < step  ? <CheckCircle size={12} className="text-blue-400" />
                               : i === step ? <Loader2 size={12} className="animate-spin" />
                               : <div className="w-2 h-2 rounded-full bg-zinc-800" />}
                            </div>
                            {s}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────
const AndromedaSimulatorTab = ({ filmData, saveFilmData, setNotification, credits, deductCredit }) => {
    const audienceAnalysis = filmData?.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis || null;

    // Form state
    const [dailyBudget,    setDailyBudget]    = useState(400);
    const [totalDays,      setTotalDays]      = useState(21);
    const [assetCount,     setAssetCount]     = useState(4);
    const [selectedPlats,  setSelectedPlats]  = useState(['Instagram', 'Facebook']);
    const [objective,      setObjective]      = useState('video_views');
    const [assetMix,       setAssetMix]       = useState({ vertical: 2, carousel: 1, static: 1 });

    // Simulator result state
    const [result,    setResult]    = useState(null);
    const [loading,   setLoading]   = useState(false);
    const [activeTab, setActiveTab] = useState('phases');

    // Track last campaign inputs so PDF export can reference them
    const lastCampaignInputsRef = React.useRef(null);

    // Debrief state
    const [mode,          setMode]         = useState('simulator'); // 'simulator' | 'debrief'
    const [assetRows,     setAssetRows]    = useState([EMPTY_ASSET()]);
    const [debriefResult, setDebriefResult] = useState(null);
    const [debriefLoading, setDebriefLoading] = useState(false);
    const [debriefTab,    setDebriefTab]   = useState('rankings');

    const totalBudget    = dailyBudget * totalDays;
    const budgetPerAsset = Math.round(dailyBudget / Math.max(assetCount, 1));
    const hasAndromeda   = !!audienceAnalysis;

    const togglePlatform = (p) => {
        setSelectedPlats(prev =>
            prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
        );
    };

    const updateMix = (key, val) => {
        const num = Math.max(0, parseInt(val) || 0);
        setAssetMix(prev => {
            const next = { ...prev, [key]: num };
            // Keep total in sync with assetCount
            const total = Object.values(next).reduce((a, b) => a + b, 0);
            setAssetCount(total);
            return next;
        });
    };

    const handleRun = async () => {
        if (!hasAndromeda) {
            setNotification({ type: 'error', message: 'Run Audience Genius first — Andromeda hooks are required.' });
            return;
        }
        if (credits < 3) {
            setNotification({ type: 'error', message: 'Insufficient credits. Requires 3.' });
            return;
        }
        if (selectedPlats.length === 0) {
            setNotification({ type: 'error', message: 'Select at least one platform.' });
            return;
        }

        setLoading(true);
        try {
            const campaignInputs = {
                dailyBudget,
                totalDays,
                assetCount,
                platforms: selectedPlats,
                assetMix,
                objective,
            };
            lastCampaignInputsRef.current = campaignInputs;
            const data = await generateAndromedaSimulationLLM(filmData, campaignInputs, audienceAnalysis);
            await deductCredit(3);
            setResult(data);
            setActiveTab('phases');
            setNotification({ type: 'success', message: 'Andromeda simulation complete.' });
        } catch (e) {
            console.error(e);
            setNotification({ type: 'error', message: e.message || 'Simulation failed.' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportSimulatorPDF = () => {
    try {
        if (!window.jspdf || !result) {
            setNotification({ type: 'error', message: 'PDF engine not ready or missing data.' });
            return;
        }

        const simulationData = result;
        const campaignInputs = lastCampaignInputsRef.current || {};

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 50;
        let y = margin;

        const C = {
            AMBER: [245, 158, 11],
            DARK: [24, 24, 27],
            GRAY: [113, 113, 122],
            BG_SHADE: [248, 250, 252],
            LINE: [226, 232, 240]
        };

        const checkPageBreak = (needed) => {
            if (y + needed > pageHeight - margin) {
                doc.addPage();
                y = margin;
                return true;
            }
            return false;
        };

        const drawHeader = (title) => {
            checkPageBreak(60);
            y += 20;
            doc.setFillColor(...C.AMBER);
            doc.rect(margin, y, 4, 18, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(...C.DARK);
            doc.text(title.toUpperCase(), margin + 12, y + 14);
            y += 35;
        };

        const addTextBlock = (text, indent = margin) => {
            if (!text) return;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(...C.DARK);
            const lines = doc.splitTextToSize(text, pageWidth - margin - indent);
            checkPageBreak(lines.length * 14 + 10);
            doc.text(lines, indent, y);
            y += (lines.length * 14) + 10;
        };

        const addLabelValue = (label, value, indent = margin) => {
            if (!value) return;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(...C.GRAY);
            doc.text(label.toUpperCase() + ":", indent, y);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...C.DARK);
            const vLines = doc.splitTextToSize(value, pageWidth - margin - indent - 90);
            doc.text(vLines, indent + 90, y);
            y += (vLines.length * 14) + 5;
        };

        // 1. COVER HEADER
        doc.setFillColor(...C.DARK);
        doc.rect(0, 0, pageWidth, 130, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont("helvetica", "bold");
        doc.text("ANDROMEDA REACH SIMULATION", margin, 60);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(180, 180, 180);
        doc.text(`PROJECT: ${(filmData?.title || "UNTITLED").toUpperCase()}`, margin, 85);
        doc.text(`BUDGET VELOCITY: $${campaignInputs?.dailyBudget || 0}/DAY FOR ${campaignInputs?.totalDays || 0} DAYS`, margin, 105);

        y = 160;

        // 2. CAMPAIGN PORTRAIT
        if (simulationData.campaign_portrait) {
            drawHeader("Campaign Verdict");
            doc.setFont("helvetica", "bold"); 
            doc.setFontSize(12);
            doc.setTextColor(...C.AMBER);
            const verdictLines = doc.splitTextToSize(simulationData.campaign_portrait.one_line_verdict, pageWidth - margin * 2);
            doc.text(verdictLines, margin, y);
            y += (verdictLines.length * 16) + 15;

            addLabelValue("Budget Class", simulationData.campaign_portrait.budget_class);
            addLabelValue("Learning Exit", simulationData.campaign_portrait.learning_exit_estimate);
            addLabelValue("Biggest Risk", simulationData.campaign_portrait.biggest_risk);
        }

        // 3. PHASE BREAKDOWN
        if (simulationData.phase_breakdown?.length > 0) {
            drawHeader("Phase-by-Phase Forecast");
            simulationData.phase_breakdown.forEach(phase => {
                checkPageBreak(120);
                doc.setFillColor(...C.DARK);
                doc.rect(margin, y, pageWidth - margin * 2, 24, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text(`${phase.phase_name.toUpperCase()}  |  ${phase.days}`, margin + 8, y + 16);
                y += 35;

                addLabelValue("Algorithm Behavior", phase.what_andromeda_is_doing);
                addLabelValue("What You'll See", phase.what_you_will_see);
                
                y += 10;
                doc.setFont("helvetica", "bold"); doc.setTextColor(...C.GRAY); doc.text("EXPECTED METRICS:", margin, y); y += 15;
                addLabelValue("CPR Range", phase.expected_metrics.cpr_range, margin + 10);
                addLabelValue("Est. Reach", phase.expected_metrics.reach, margin + 10);
                
                y += 10;
                doc.setFont("helvetica", "bold"); doc.setTextColor(...C.GRAY); doc.text("DECISION POINT:", margin, y); y += 15;
                addTextBlock(phase.decision, margin + 10);
                y += 15;
            });
        }

        // 4. HEALTH DASHBOARD
        if (simulationData.health_dashboard) {
            drawHeader("Health Dashboard Protocol");
            
            doc.setFillColor(...C.BG_SHADE);
            doc.rect(margin, y, pageWidth - margin * 2, 40, 'F');
            doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...C.DARK);
            doc.text("PRIMARY KPI:", margin + 10, y + 16);
            doc.setFont("helvetica", "normal"); doc.setTextColor(...C.AMBER);
            doc.text(simulationData.health_dashboard.primary_kpi, margin + 90, y + 16);
            doc.setFont("helvetica", "bold"); doc.setTextColor(...C.DARK);
            doc.text("REAL SIGNAL:", margin + 10, y + 30);
            doc.setFont("helvetica", "normal"); doc.setTextColor(...C.GRAY);
            doc.text(simulationData.health_dashboard.the_real_signal, margin + 90, y + 30);
            y += 60;

            if (simulationData.health_dashboard.watch_list?.length > 0) {
                simulationData.health_dashboard.watch_list.forEach(item => {
                    checkPageBreak(50);
                    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...C.DARK);
                    doc.text(item.metric.toUpperCase(), margin, y); y += 12;
                    doc.setFontSize(9); doc.setTextColor(...C.GRAY);
                    doc.text(`HEALTHY: ${item.healthy_range}  |  DANGER: ${item.danger_zone}`, margin, y); y += 14;
                    addTextBlock(item.what_it_means);
                    y += 5;
                });
            }
        }

        // FOOTER
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(...C.GRAY);
            doc.text(`BLACKBOX Andromeda Intelligence | Confidential | Page ${i}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
        }

        doc.save(`${filmData?.title ? filmData.title.replace(/\s+/g, '_') : 'Project'}_Andromeda_Simulation.pdf`);
        setNotification({ type: 'success', message: 'Simulation PDF exported.' });

    } catch (error) {
        console.error("PDF Error:", error);
        setNotification({ type: 'error', message: 'PDF Export failed.' });
    }
};

    // ── DEBRIEF HELPERS ──────────────────────────────────────────
    const addAsset = () => setAssetRows(prev => [...prev, EMPTY_ASSET()]);
    const removeAsset = (id) => setAssetRows(prev => prev.filter(a => a.id !== id));
    const updateAsset = (id, updated) => setAssetRows(prev => prev.map(a => a.id === id ? updated : a));

    const hookNames = audienceAnalysis?.creative_matrix?.map(m => m.hook_name) || [];

    const handleRunDebrief = async () => {
        if (!hasAndromeda) {
            setNotification({ type: 'error', message: 'Run Audience Genius first.' });
            return;
        }
        if (credits < 3) {
            setNotification({ type: 'error', message: 'Insufficient credits. Requires 3.' });
            return;
        }
        const valid = assetRows.filter(a => a.name.trim() && a.spend);
        if (valid.length === 0) {
            setNotification({ type: 'error', message: 'Enter at least one asset with a name and spend amount.' });
            return;
        }
        setDebriefLoading(true);
        try {
            const campaignInputs = { dailyBudget, totalDays, platforms: selectedPlats, objective };
            const data = await generateAndromedaDebriefLLM(filmData, campaignInputs, valid, audienceAnalysis);
            await deductCredit(3);
            setDebriefResult(data);
            setDebriefTab('rankings');
            setNotification({ type: 'success', message: 'Campaign debrief complete.' });
        } catch (e) {
            console.error(e);
            setNotification({ type: 'error', message: e.message || 'Debrief failed.' });
        } finally {
            setDebriefLoading(false);
        }
    };

    const DEBRIEF_TABS = [
        { id: 'rankings',    label: 'Asset Rankings',      icon: Trophy    },
        { id: 'realloc',     label: 'Budget Reallocation', icon: DollarSign },
        { id: 'next',        label: 'Next Campaign',       icon: Zap       },
        { id: 'audience',    label: 'Audience Intel',      icon: Brain     },
    ];

    const RESULT_TABS = [
        { id: 'phases',    label: 'Phase Forecast',     icon: Activity },
        { id: 'scenarios', label: 'Outcomes',            icon: BarChart2 },
        { id: 'dashboard', label: 'Health Dashboard',    icon: Eye },
        { id: 'triggers',  label: 'Decision Triggers',  icon: Radio },
        { id: 'harvest',   label: 'Intelligence',        icon: Brain },
    ];

    return (
        <div className="h-full flex flex-col gap-0">
            <SimulatorOverlay isVisible={loading} />

            {/* ── PAGE HEADER ─────────────────────────────────────────── */}
            <div className="flex items-start justify-between mb-6 pb-6 border-b border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-amber-500/10 rounded-xl flex items-center justify-center border border-amber-500/20">
                        <Zap size={20} className="text-amber-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">Andromeda Reach Simulator</h2>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">
                            {mode === 'simulator' ? 'Campaign configuration → Phase forecast → Decision playbook' : 'Post-campaign debrief → Asset rankings → Next campaign brief'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
                    <button onClick={() => setMode('simulator')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${mode === 'simulator' ? 'bg-amber-500 text-black' : 'text-zinc-500 hover:text-zinc-300'}`}>
                        <Zap size={12} /> Simulator
                    </button>
                    <button onClick={() => setMode('debrief')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${mode === 'debrief' ? 'bg-blue-500 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
                        <FlaskConical size={12} /> Debrief
                    </button>
                        {result && (
                            <AppButton 
                                onClick={handleExportSimulatorPDF} 
                                variant="primary" 
                                size="sm" 
                                icon={Download}
                            >
                                Export PDF
                            </AppButton>
                        )}
                    
                </div>
                {!hasAndromeda && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                        <AlertTriangle size={12} className="text-amber-400 shrink-0" />
                        <span className="text-xs text-amber-300">Run Audience Genius first</span>
                    </div>
                )}
            </div>

            {/* ══ DEBRIEF MODE ═══════════════════════════════════════════ */}
            {mode === 'debrief' && (
                <div className="flex flex-col xl:flex-row gap-6 flex-1 min-h-0">
                    <DebriefOverlay isVisible={debriefLoading} />

                    {/* LEFT: DEBRIEF CONFIG */}
                    <div className="xl:w-[300px] shrink-0 flex flex-col gap-5">
                        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-3">
                            <SectionHeader label="Campaign That Ran" icon={Activity} color="text-blue-400" />
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Daily Budget</p>
                                    <div className="flex items-center gap-1">
                                        <span className="text-zinc-600 text-xs">$</span>
                                        <input type="number" value={dailyBudget} onChange={e => setDailyBudget(Number(e.target.value))}
                                            className="w-full bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/40" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Duration (days)</p>
                                    <input type="number" value={totalDays} onChange={e => setTotalDays(Number(e.target.value))}
                                        className="w-full bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/40" />
                                </div>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Platforms</p>
                                <div className="flex gap-2 flex-wrap">
                                    {PLATFORMS.map(p => (
                                        <button key={p} onClick={() => togglePlatform(p)}
                                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${selectedPlats.includes(p) ? 'bg-blue-500/15 border-blue-500/40 text-blue-200' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
                                            {p}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">Objective</p>
                                <select value={objective} onChange={e => setObjective(e.target.value)}
                                    className="w-full bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/40">
                                    {OBJECTIVES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <button onClick={handleRunDebrief} disabled={debriefLoading || !hasAndromeda}
                            className="w-full py-4 bg-blue-500 hover:bg-blue-400 text-white font-black rounded-xl transition-all shadow-[0_4px_24px_rgba(59,130,246,0.3)] disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]">
                            <span className="flex items-center justify-center gap-2 uppercase tracking-widest text-sm">
                                <FlaskConical size={16} />
                                {debriefLoading ? 'Analyzing...' : 'Run Debrief'}
                                <span className="text-white/60 font-normal normal-case tracking-normal text-xs">(3 cr)</span>
                            </span>
                        </button>

                        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-2">
                            <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">How to fill this in</p>
                            <p className="text-[10px] text-zinc-500 leading-relaxed">In Meta Ads Manager, go to <span className="text-zinc-300">Ads → Columns → Customize</span> and add: Results, Reach, Frequency, CPR, CTR, Shares, Saves, ThruPlay Rate, Video 75%, Video 95%. Export as CSV or copy values per ad.</p>
                        </div>

                        {hookNames.length > 0 && (
                            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
                                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Available Hooks</p>
                                {hookNames.map((h, i) => (
                                    <p key={i} className="text-[10px] text-zinc-400 flex gap-1.5 mb-1">
                                        <span className="text-amber-500/60 font-bold">{i+1}.</span>{h}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* RIGHT: ASSET DATA ENTRY + RESULTS */}
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        {!debriefResult ? (
                            <div className="flex flex-col flex-1 overflow-y-auto custom-scrollbar space-y-4 pb-8">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-white">Asset Performance Data</h3>
                                    <button onClick={addAsset}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-xs font-bold text-zinc-300 transition-colors">
                                        <Plus size={12} /> Add Asset
                                    </button>
                                </div>
                                {assetRows.map((asset, i) => (
                                    <AssetRow key={asset.id} asset={asset} index={i}
                                        hooks={hookNames}
                                        onChange={updated => updateAsset(asset.id, updated)}
                                        onRemove={() => removeAsset(asset.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col h-full overflow-hidden">
                                {/* Verdict header */}
                                <div className="mb-5 p-5 rounded-xl border flex items-center gap-5 flex-shrink-0 flex-wrap gap-y-3 bg-zinc-900/40 border-zinc-800">
                                    <div className="w-14 h-14 rounded-full border-2 border-zinc-700 flex items-center justify-center shrink-0">
                                        <span className="text-2xl font-black text-white">{debriefResult.campaign_verdict?.overall_grade}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-white text-sm leading-snug mb-1">{debriefResult.campaign_verdict?.one_line_verdict}</p>
                                        <p className="text-xs text-zinc-400 leading-relaxed">{debriefResult.campaign_verdict?.algorithm_behavior}</p>
                                    </div>
                                    <div className="flex flex-col gap-2 shrink-0">
                                        <div className="flex flex-wrap gap-1.5">
                                            {debriefResult.campaign_verdict?.hooks_validated?.map((h, i) => (
                                                <span key={i} className="text-[9px] font-bold bg-green-500/10 text-green-300 border border-green-500/25 px-2 py-0.5 rounded-full">✓ {h}</span>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {debriefResult.campaign_verdict?.hooks_rejected?.map((h, i) => (
                                                <span key={i} className="text-[9px] font-bold bg-red-500/10 text-red-300 border border-red-500/25 px-2 py-0.5 rounded-full">✗ {h}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <button onClick={() => setDebriefResult(null)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-zinc-600 hover:text-zinc-400 transition-colors shrink-0">
                                        <RefreshCw size={11} /> Re-enter data
                                    </button>
                                </div>

                                {/* Debrief tabs */}
                                <div className="flex gap-1 mb-5 flex-shrink-0 border-b border-zinc-800 overflow-x-auto">
                                    {DEBRIEF_TABS.map(({ id, label, icon: Icon }) => (
                                        <button key={id} onClick={() => setDebriefTab(id)}
                                            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-all whitespace-nowrap ${debriefTab === id ? 'border-blue-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                                            <Icon size={12} className={debriefTab === id ? 'text-blue-400' : ''} />
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {/* Debrief tab content */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar pb-8 space-y-4">

                                    {/* ASSET RANKINGS */}
                                    {debriefTab === 'rankings' && (
                                        <div className="space-y-3">
                                            {debriefResult.asset_rankings?.map((asset, i) => (
                                                <AssetRankCard key={i} asset={asset} />
                                            ))}
                                        </div>
                                    )}

                                    {/* BUDGET REALLOCATION */}
                                    {debriefTab === 'realloc' && debriefResult.budget_reallocation && (
                                        <div className="space-y-4">
                                            <div className="p-5 bg-blue-500/5 border border-blue-500/20 rounded-xl">
                                                <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest mb-2">Directive</p>
                                                <p className="text-sm text-zinc-200 leading-relaxed">{debriefResult.budget_reallocation.directive}</p>
                                            </div>
                                            <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Recommended Split</p>
                                                <p className="text-sm font-bold text-white leading-relaxed">{debriefResult.budget_reallocation.recommended_split}</p>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {debriefResult.budget_reallocation.kill_list?.length > 0 && (
                                                    <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
                                                        <p className="text-[9px] font-bold text-red-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><XCircle size={9} /> Kill</p>
                                                        {debriefResult.budget_reallocation.kill_list.map((a, i) => (
                                                            <p key={i} className="text-xs text-zinc-400 flex gap-2 mb-1"><span className="text-red-400 shrink-0">×</span>{a}</p>
                                                        ))}
                                                    </div>
                                                )}
                                                {debriefResult.budget_reallocation.scale_list?.length > 0 && (
                                                    <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-xl">
                                                        <p className="text-[9px] font-bold text-green-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><TrendingUp size={9} /> Scale</p>
                                                        {debriefResult.budget_reallocation.scale_list.map((a, i) => (
                                                            <p key={i} className="text-xs text-zinc-400 flex gap-2 mb-1"><span className="text-green-400 shrink-0">↑</span>{a}</p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {debriefResult.budget_reallocation.remix_list?.length > 0 && (
                                                <div className="space-y-2">
                                                    <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest flex items-center gap-1.5"><Scissors size={9} /> Remix Directives</p>
                                                    {debriefResult.budget_reallocation.remix_list.map((item, i) => (
                                                        <div key={i} className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
                                                            <p className="text-xs font-bold text-white mb-1">{item.asset}</p>
                                                            <p className="text-[11px] text-blue-200 mb-1"><span className="font-bold">Change: </span>{item.what_to_change}</p>
                                                            <p className="text-[11px] text-zinc-400">{item.why}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* NEXT CAMPAIGN */}
                                    {debriefTab === 'next' && debriefResult.next_campaign && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <div className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                    <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Hook Strategy</p>
                                                    <p className="text-xs text-zinc-300 leading-relaxed">{debriefResult.next_campaign.hook_strategy}</p>
                                                </div>
                                                <div className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                    <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Format Strategy</p>
                                                    <p className="text-xs text-zinc-300 leading-relaxed">{debriefResult.next_campaign.format_strategy}</p>
                                                </div>
                                                <div className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                    <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Budget Velocity</p>
                                                    <p className="text-xs text-zinc-300 leading-relaxed">{debriefResult.next_campaign.budget_velocity}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-3">New Asset Briefs</p>
                                                <div className="space-y-3">
                                                    {debriefResult.next_campaign.new_asset_briefs?.map((brief, i) => (
                                                        <div key={i} className="p-5 bg-zinc-950 border border-zinc-800 rounded-xl hover:border-blue-500/25 transition-colors">
                                                            <div className="flex items-center gap-3 mb-3">
                                                                <div className="w-6 h-6 rounded-full bg-blue-500/15 border border-blue-500/30 text-[10px] font-black text-blue-400 flex items-center justify-center shrink-0">{i+1}</div>
                                                                <h4 className="font-bold text-white text-sm">{brief.brief_name}</h4>
                                                                <Tag variant="blue">{brief.format}</Tag>
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                <div>
                                                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Hook</p>
                                                                    <p className="text-xs text-amber-300">{brief.hook}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Opening 3 Seconds</p>
                                                                    <p className="text-xs text-zinc-300 leading-relaxed">{brief.opening_seconds}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1">What to Capture</p>
                                                                    <p className="text-xs text-zinc-300 leading-relaxed">{brief.what_to_capture}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Hypothesis</p>
                                                                    <p className="text-xs text-zinc-400 italic leading-relaxed">{brief.hypothesis}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="p-5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Expected Improvement</p>
                                                <p className="text-sm text-zinc-200 leading-relaxed">{debriefResult.next_campaign.expected_improvement}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* AUDIENCE INTEL */}
                                    {debriefTab === 'audience' && debriefResult.audience_intelligence && (
                                        <div className="space-y-4">
                                            <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Who Andromeda Actually Found</p>
                                                <p className="text-sm text-zinc-300 leading-relaxed">{debriefResult.audience_intelligence.who_andromeda_found}</p>
                                            </div>
                                            <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Psychographic Confirmation</p>
                                                <p className="text-sm text-zinc-300 leading-relaxed">{debriefResult.audience_intelligence.psychographic_confirmation}</p>
                                            </div>
                                            {debriefResult.audience_intelligence.unexpected_signals?.length > 0 && (
                                                <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                    <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Unexpected Signals</p>
                                                    {debriefResult.audience_intelligence.unexpected_signals.map((s, i) => (
                                                        <p key={i} className="text-xs text-zinc-300 flex gap-2 mb-2"><span className="text-amber-500/60 shrink-0">›</span>{s}</p>
                                                    ))}
                                                </div>
                                            )}
                                            {debriefResult.audience_intelligence.data_gaps?.length > 0 && (
                                                <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                    <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-3">Data Gaps — Design Next Campaign to Answer</p>
                                                    {debriefResult.audience_intelligence.data_gaps.map((g, i) => (
                                                        <p key={i} className="text-xs text-zinc-400 flex gap-2 mb-2"><span className="text-blue-400 shrink-0">?</span>{g}</p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══ SIMULATOR MODE ══════════════════════════════════════════ */}
            {mode === 'simulator' && <div className="flex flex-col xl:flex-row gap-6 flex-1 min-h-0">

                {/* ── LEFT: CONFIG PANEL ──────────────────────────────── */}
                <div className="xl:w-[300px] shrink-0 flex flex-col gap-5">

                    {/* Budget & Duration */}
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-4">
                        <SectionHeader label="Campaign Budget" icon={DollarSign} />

                        <div>
                            <div className="flex justify-between items-baseline mb-2">
                                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Daily Budget</label>
                                <span className="text-lg font-black text-amber-300">${dailyBudget.toLocaleString()}</span>
                            </div>
                            <input type="range" min="50" max="2000" step="50"
                                value={dailyBudget}
                                onChange={e => setDailyBudget(Number(e.target.value))}
                                className="w-full accent-amber-500 h-1.5 rounded-full"
                            />
                            <div className="flex justify-between text-[9px] text-zinc-700 mt-1">
                                <span>$50</span><span>$2,000</span>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-baseline mb-2">
                                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Campaign Duration</label>
                                <span className="text-lg font-black text-white">{totalDays} days</span>
                            </div>
                            <input type="range" min="7" max="90" step="1"
                                value={totalDays}
                                onChange={e => setTotalDays(Number(e.target.value))}
                                className="w-full accent-amber-500 h-1.5 rounded-full"
                            />
                            <div className="flex justify-between text-[9px] text-zinc-700 mt-1">
                                <span>7d</span><span>90d</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-zinc-800">
                            <div className="bg-black/30 rounded-lg p-3 text-center">
                                <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-1">Total Spend</p>
                                <p className="text-base font-black text-white">${totalBudget.toLocaleString()}</p>
                            </div>
                            <div className="bg-black/30 rounded-lg p-3 text-center">
                                <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-1">Per Asset/Day</p>
                                <p className="text-base font-black text-amber-300">${budgetPerAsset}</p>
                            </div>
                        </div>
                    </div>

                    {/* Asset Mix */}
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <SectionHeader label="Asset Mix" icon={Layers} />
                            <span className="text-[10px] text-zinc-600 font-mono -mt-4">{assetCount} total</span>
                        </div>
                        {[
                            { key: 'vertical',  label: 'Vertical Video', color: 'accent-blue-500' },
                            { key: 'carousel',  label: 'Carousels',      color: 'accent-purple-500' },
                            { key: 'static',    label: 'Static Images',  color: 'accent-teal-500' },
                        ].map(({ key, label, color }) => (
                            <div key={key}>
                                <div className="flex justify-between items-center mb-1.5">
                                    <label className="text-[10px] font-bold text-zinc-500">{label}</label>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => updateMix(key, assetMix[key] - 1)}
                                            className="w-5 h-5 rounded bg-zinc-800 text-zinc-400 text-xs font-bold hover:bg-zinc-700 transition-colors">−</button>
                                        <span className="text-sm font-bold text-white w-4 text-center">{assetMix[key]}</span>
                                        <button onClick={() => updateMix(key, assetMix[key] + 1)}
                                            className="w-5 h-5 rounded bg-zinc-800 text-zinc-400 text-xs font-bold hover:bg-zinc-700 transition-colors">+</button>
                                    </div>
                                </div>
                                {assetCount > 0 && (
                                    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                                        <div className={`h-full bg-current transition-all`}
                                            style={{ width: `${(assetMix[key] / assetCount) * 100}%` }} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Platforms */}
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-3">
                        <SectionHeader label="Platforms" icon={Radio} />
                        <div className="flex flex-col gap-2">
                            {PLATFORMS.map(p => (
                                <button key={p}
                                    onClick={() => togglePlatform(p)}
                                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                                        selectedPlats.includes(p)
                                            ? 'bg-amber-500/10 border-amber-500/40 text-amber-200'
                                            : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-700'
                                    }`}
                                >
                                    {p}
                                    {selectedPlats.includes(p) && <CheckCircle size={13} className="text-amber-400" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Objective */}
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-3">
                        <SectionHeader label="Optimization Objective" icon={Target} />
                        <div className="flex flex-col gap-2">
                            {OBJECTIVES.map(o => (
                                <button key={o.value}
                                    onClick={() => setObjective(o.value)}
                                    className={`flex flex-col items-start px-3 py-3 rounded-lg border text-left transition-all ${
                                        objective === o.value
                                            ? 'bg-amber-500/10 border-amber-500/40'
                                            : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
                                    }`}
                                >
                                    <span className={`text-xs font-bold ${objective === o.value ? 'text-amber-200' : 'text-zinc-400'}`}>{o.label}</span>
                                    <span className="text-[10px] text-zinc-600 mt-0.5">{o.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Cast & Key Talent */}
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-3">
                        <SectionHeader label="Cast & Key Talent" icon={Film} />
                        <div>
                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest block mb-1.5">
                                Featured Cast
                            </label>
                            <textarea
                                value={filmData?.stars || ''}
                                onChange={(e) => saveFilmData({ ...filmData, stars: e.target.value })}
                                placeholder="e.g., Naturi Naughton, Lance Gross, Jason Mitchell..."
                                rows={2}
                                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500/40 placeholder:text-zinc-700 resize-none"
                            />
                            <p className="text-[9px] text-zinc-600 mt-1 leading-snug">Cast info helps Andromeda target fans of these performers.</p>
                        </div>
                    </div>

                    {/* Run Button */}
                    <button
                        onClick={handleRun}
                        disabled={loading || !hasAndromeda || selectedPlats.length === 0}
                        className="group w-full py-4 bg-amber-500 hover:bg-amber-400 text-black font-black rounded-xl transition-all shadow-[0_4px_24px_rgba(245,158,11,0.3)] hover:shadow-[0_4px_32px_rgba(245,158,11,0.5)] disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
                    >
                        <span className="flex items-center justify-center gap-2 uppercase tracking-widest text-sm">
                            <Zap size={16} className={`${loading ? 'animate-pulse' : 'fill-current'}`} />
                            {loading ? 'Simulating...' : 'Run Simulation'}
                            <span className="text-black/60 font-normal normal-case tracking-normal text-xs">(3 cr)</span>
                        </span>
                    </button>

                    {/* Andromeda hooks summary if loaded */}
                    {hasAndromeda && audienceAnalysis.creative_matrix?.length > 0 && (
                        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
                            <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2.5">Active Andromeda Hooks</p>
                            <div className="space-y-1.5">
                                {audienceAnalysis.creative_matrix.map((m, i) => (
                                    <div key={i} className="flex gap-2 text-[10px]">
                                        <span className="text-amber-500/60 font-bold shrink-0">{i+1}.</span>
                                        <span className="text-zinc-400 leading-snug">{m.hook_name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── RIGHT: RESULTS PANEL ────────────────────────────── */}
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {!result ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                            <div className="w-24 h-24 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6 relative">
                                <div className="absolute inset-0 border border-amber-500/10 rounded-full animate-[ping_4s_infinite]" />
                                <Zap size={32} className="text-zinc-700" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Configure Your Campaign</h3>
                            <p className="text-zinc-500 max-w-sm text-sm leading-relaxed mb-2">
                                Set your budget, asset mix, platforms, and objective on the left. The simulator will model exactly how Andromeda will behave with your specific inputs.
                            </p>
                            <p className="text-zinc-600 text-xs max-w-xs leading-relaxed">
                                Requires Audience Genius to have been run first — the Creative Matrix hooks are what the simulation is built around.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full overflow-hidden">
                            {/* Campaign Portrait */}
                            <div className="flex-shrink-0 mb-5">
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                    <div className="md:col-span-2 p-5 bg-amber-500/5 border border-amber-500/25 rounded-xl flex flex-col gap-2">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Tag variant="amber">{result.campaign_portrait?.budget_class}</Tag>
                                        </div>
                                        <p className="text-sm font-bold text-white leading-snug">{result.campaign_portrait?.one_line_verdict}</p>
                                        <p className="text-[10px] text-zinc-500 mt-auto">
                                            <span className="text-amber-400 font-bold">Learning exit: </span>
                                            {result.campaign_portrait?.learning_exit_estimate}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-green-500/5 border border-green-500/15 rounded-xl">
                                        <p className="text-[9px] font-bold text-green-400 uppercase tracking-widest mb-2">Algorithm Advantage</p>
                                        <p className="text-xs text-zinc-300 leading-relaxed">{result.campaign_portrait?.algorithm_advantage}</p>
                                    </div>
                                    <div className="md:col-span-2 p-4 bg-red-500/5 border border-red-500/15 rounded-xl">
                                        <p className="text-[9px] font-bold text-red-400 uppercase tracking-widest mb-2">Biggest Risk</p>
                                        <p className="text-xs text-zinc-300 leading-relaxed">{result.campaign_portrait?.biggest_risk}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Tab nav */}
                            <div className="flex gap-1 mb-5 flex-shrink-0 border-b border-zinc-800 pb-0 overflow-x-auto">
                                {RESULT_TABS.map(({ id, label, icon: Icon }) => (
                                    <button key={id}
                                        onClick={() => setActiveTab(id)}
                                        className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-all whitespace-nowrap ${
                                            activeTab === id
                                                ? 'border-amber-500 text-white'
                                                : 'border-transparent text-zinc-500 hover:text-zinc-300'
                                        }`}
                                    >
                                        <Icon size={12} className={activeTab === id ? 'text-amber-400' : ''} />
                                        {label}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setResult(null)}
                                    className="ml-auto flex items-center gap-1.5 px-4 py-3 text-xs font-bold text-zinc-600 hover:text-zinc-400 transition-colors whitespace-nowrap"
                                >
                                    <RefreshCw size={11} /> Reset
                                </button>
                            </div>

                            {/* Tab content — scrollable */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar pb-8 space-y-4">

                                {/* PHASES */}
                                {activeTab === 'phases' && (
                                    <div className="space-y-3">
                                        {result.phase_breakdown?.map((phase, i) => (
                                            <PhaseCard key={i} phase={phase} index={i} />
                                        ))}
                                    </div>
                                )}

                                {/* SCENARIOS */}
                                {activeTab === 'scenarios' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <ScenarioCard label="Conservative" data={result.outcome_scenarios?.conservative} variant="conservative" />
                                        <ScenarioCard label="Realistic"    data={result.outcome_scenarios?.realistic}    variant="realistic" />
                                        <ScenarioCard label="Breakout"     data={result.outcome_scenarios?.breakout}     variant="breakout" />
                                    </div>
                                )}

                                {/* DASHBOARD */}
                                {activeTab === 'dashboard' && result.health_dashboard && (
                                    <div className="space-y-5">
                                        {/* Primary KPI */}
                                        <div className="p-5 bg-amber-500/8 border border-amber-500/25 rounded-xl">
                                            <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">Primary KPI for this campaign</p>
                                            <p className="text-lg font-bold text-white">{result.health_dashboard.primary_kpi}</p>
                                        </div>

                                        {/* Watch list table */}
                                        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
                                            <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-zinc-900/60 border-b border-zinc-800">
                                                <div className="col-span-1" />
                                                <div className="col-span-3 text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Metric</div>
                                                <div className="col-span-2 text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Healthy</div>
                                                <div className="col-span-2 text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Danger</div>
                                                <div className="col-span-4 text-[9px] font-bold text-zinc-500 uppercase tracking-widest">What it means</div>
                                            </div>
                                            <div className="px-5">
                                                {result.health_dashboard.watch_list?.map((item, i) => (
                                                    <WatchRow key={i} item={item} index={i} />
                                                ))}
                                            </div>
                                        </div>

                                        {/* The real signal */}
                                        <div className="flex items-start gap-3 p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                            <MessageSquare size={14} className="text-amber-400 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-1.5">The Real Signal — Off Dashboard</p>
                                                <p className="text-sm text-zinc-200 leading-relaxed">{result.health_dashboard.the_real_signal}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* TRIGGERS */}
                                {activeTab === 'triggers' && (
                                    <div className="space-y-6">
                                        {result.decision_triggers?.map((item, i) => (
                                            <DecisionTrigger key={i} item={item} index={i} />
                                        ))}
                                    </div>
                                )}

                                {/* HARVEST */}
                                {activeTab === 'harvest' && result.intelligence_harvest && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">What you will know after this campaign</p>
                                                <p className="text-sm text-zinc-300 leading-relaxed">{result.intelligence_harvest.what_you_will_know}</p>
                                            </div>
                                            <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">How to identify the winning asset</p>
                                                <p className="text-sm text-zinc-300 leading-relaxed">{result.intelligence_harvest.winning_asset_signal}</p>
                                            </div>
                                        </div>
                                        <div className="p-5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                                            <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">How to use this for your next campaign</p>
                                            <p className="text-sm text-zinc-300 leading-relaxed">{result.intelligence_harvest.next_campaign_inputs}</p>
                                        </div>
                                        {result.intelligence_harvest.data_to_export?.length > 0 && (
                                            <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                    <Download size={10} /> Export before data ages out
                                                </p>
                                                <div className="space-y-2">
                                                    {result.intelligence_harvest.data_to_export.map((item, i) => (
                                                        <div key={i} className="flex gap-2 text-xs text-zinc-300">
                                                            <CheckCircle size={11} className="text-green-500 shrink-0 mt-0.5" />
                                                            {item}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>}
        </div>
    );
};

export default AndromedaSimulatorTab;